import type {
  AccidentRecord, AccidentRecordCallback, AccidentRelation, AccidentRole, AccidentWhy, AgreementState, LeadAccident, LeadCard,
  LeadFields, LeadSibling, RecordField,
} from "@/lib/types";

/**
 * How a contact's records relate, as a person says (ACL-337, ADR-120 in the platform repo): the same
 * accident again, another accident of the same person, or another person in the same accident. The
 * words the record, the phone and their tests share. Pure.
 *
 * Nothing here says two records are the same person, and nothing is decided for the rep: a record with
 * the phone or the e-mail is listed, and stays "not said yet" until somebody says.
 */

/** A party's role, in the order the picker lists them (the people and accidents model's words). */
export const ACCIDENT_ROLES: readonly AccidentRole[] = ["driver", "passenger", "pedestrian", "owner", "guardian"];

const ROLE_WORDS: Record<AccidentRole, string> = {
  driver: "Driver",
  passenger: "Passenger",
  pedestrian: "Pedestrian",
  owner: "Owner",
  guardian: "Guardian",
};

export function roleText(role: AccidentRole | null | undefined): string | null {
  return role ? ROLE_WORDS[role] : null;
}

const RELATION_WORDS: Record<AccidentRelation, string> = {
  same_accident: "Same accident",
  another_accident: "Another accident",
  not_said: "Not said yet",
};

export function relationText(relation: AccidentRelation): string {
  return RELATION_WORDS[relation];
}

const WHY_WORDS: Record<AccidentWhy, string> = {
  same_phone: "Same phone",
  same_email: "Same email",
  linked: "Linked",
};

/** Why the record is listed: "Same phone", "Same email", or "Linked by Ana Ruiz". Who linked it is
 *  only known where the reader may open the record. */
export function whyText(record: Pick<AccidentRecord, "why" | "linked_by">): string {
  if (record.why === "linked" && record.linked_by) return `Linked by ${record.linked_by}`;
  return WHY_WORDS[record.why];
}

/** Whose record it is, as the people table says (ADR-218): "This person" for a record of the same person,
 *  "Someone else" for another person on the phone or with the e-mail, null while not known. A record
 *  somebody linked into the accident is listed as a party already, so it is not said there. */
export function personText(record: Pick<AccidentRecord, "why" | "same_person">): string | null {
  if (record.why === "linked" || record.same_person === null) return null;
  return record.same_person ? "This person" : "Someone else";
}

/** Who said how a record with the phone or the e-mail relates: "Said by Ana Ruiz", once somebody has. */
export function saidText(record: Pick<AccidentRecord, "relation" | "linked_by">): string | null {
  if (record.relation === "not_said" || !record.linked_by) return null;
  return `Said by ${record.linked_by}`;
}

/** A shared e-mail says no more than a shared phone. */
export const SAME_EMAIL_CAVEAT = "Same email only. It may be the same person or someone else.";
/** Under the card's title. */
export const ACCIDENT_DESCRIPTION = "Which records are the same accident, as somebody here said.";
/** When nobody else is in the record's accident yet. */
export const NOBODY_LINKED = "Nobody else is in this crash yet. Add a party/case: someone new, or a contact you already have.";

/** The rep may say how a listed record relates: it is theirs to open, not deleted, and nobody has yet. */
export function canSay(record: Pick<AccidentRecord, "relation" | "may_open" | "deleted">): boolean {
  return record.may_open && !record.deleted && record.relation === "not_said";
}

/** Whoever may open a listed record may take it out of its accident, a rep included, as they may link
 *  it: one that is in an accident, not deleted. */
export function canUnlink(record: Pick<AccidentRecord, "relation" | "may_open" | "deleted">): boolean {
  return record.may_open && !record.deleted && record.relation !== "not_said";
}

/** The records, by why they are listed. Those with the phone are keyed by id, to sit on the card's own
 *  list of them (ACL-336); the others are the card's own sections, newest first as they came. */
export function accidentSections(accident: LeadAccident | undefined): {
  byPhone: Map<string, AccidentRecord>;
  byEmail: AccidentRecord[];
  linked: AccidentRecord[];
} {
  const records = accident?.records ?? [];
  return {
    byPhone: new Map(records.filter((r) => r.why === "same_phone").map((r) => [r.id, r])),
    byEmail: records.filter((r) => r.why === "same_email"),
    linked: records.filter((r) => r.why === "linked"),
  };
}

/** The keys of the crash's facts the card's head says: when, where, and whether the police came. */
export const CRASH_LINE_KEYS = ["date_of_incident", "city_of_accident", "mva_accident_state", "policeincident_report_completed"] as const;

/** "Sep 12, 2026 · Los Angeles, CA · Police report: Yes" from the record's fields (shared by everyone in the
 *  accident, ADR-221), or null when none of them has a value. Pure. */
export function crashLine(fields: LeadFields | undefined, text: (field: RecordField) => string | null): string | null {
  const byKey = new Map((fields?.sections ?? []).flatMap((s) => s.fields).map((f) => [f.key, f]));
  const value = (key: string) => {
    const field = byKey.get(key);
    return field ? text(field) : null;
  };
  const date = value("date_of_incident");
  const place = [value("city_of_accident"), value("mva_accident_state")].filter(Boolean).join(", ");
  const police = value("policeincident_report_completed");
  const parts = [date, place || null, police ? `Police report: ${police}` : null].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** The other people in this record's accident (ACL-671): every record said to be the same accident, the
 *  deleted ones left out. A record is listed once, under the first reason that holds (the phone, the
 *  e-mail, or the link), so a passenger on the caller's own number is a "same phone" record that is in
 *  the accident all the same; `relation` is what says so, never `why`. */
export function partiesOf(accident: LeadAccident | undefined): AccidentRecord[] {
  return (accident?.records ?? []).filter((r) => r.relation === "same_accident" && !r.deleted);
}

/** "No agreement sent yet", "Agreement sent <when> · not signed yet" or "Signed <when>", with the tone it
 *  is drawn in (ACL-671). `when` words an instant; a state with no instant is said without one. Pure. */
export function agreementText(
  agreement: AgreementState | null | undefined,
  when: (iso: string) => string,
): { text: string; tone: "muted" | "warning" | "success" } {
  if (!agreement || agreement.state === "none") return { text: "No agreement sent yet", tone: "muted" };
  if (agreement.state === "sent") {
    return { text: `Agreement sent${agreement.at ? ` ${when(agreement.at)}` : ""} · not signed yet`, tone: "warning" };
  }
  return { text: agreement.at ? `Signed ${when(agreement.at)}` : "Signed", tone: "success" };
}

/** "Callback <when> · or else <when> · yours" for a callback still to be made for a person in the accident
 *  (ACL-671); `me` is the reader's account, so their own kept callback says so. Pure. */
export function partyCallbackText(
  callback: AccidentRecordCallback | null | undefined,
  when: (iso: string) => string,
  me: number | null,
): string | null {
  if (!callback) return null;
  const parts = [`Callback ${when(callback.due_at)}`];
  if (callback.backup_at) parts.push(`or else ${when(callback.backup_at)}`);
  if (callback.rep_user_id !== null && me !== null && callback.rep_user_id === me) parts.push("yours");
  else if (callback.rep) parts.push(`kept for ${callback.rep}`);
  return parts.join(" · ");
}

/** "on Dana's number" for a person reached on the caller's own phone, else their own number, else nothing. */
export function partyNumberText(
  party: Pick<AccidentRecord, "phone">,
  caller: { phone: string; first_name: string | null },
  format: (phone: string) => string,
): string | null {
  if (!party.phone) return null;
  if (party.phone === caller.phone) return `on ${caller.first_name ?? "the caller"}'s number`;
  return format(party.phone);
}

/** The caller's other cases (ACL-671): the records with the phone the people table says are theirs, each
 *  from another accident or none, the deleted ones left out. Pure. */
export function otherCasesOf(card: Pick<LeadCard, "siblings">): LeadSibling[] {
  return (card.siblings ?? []).filter((s) => s.same_person === true && !s.deleted);
}

/** "In accident A-000123 as passenger" for the record's own line, or null when it is in none. */
export function ownText(accident: LeadAccident | undefined): string | null {
  const own = accident?.accident;
  if (!own) return null;
  const which = own.number ? `accident ${own.number}` : "an accident";
  const role = roleText(own.role);
  return role ? `In ${which} as ${role.toLowerCase()}` : `In ${which}`;
}
